import { PropsWithChildren, useRef } from "react";
import { Box } from "@chakra-ui/react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { RichTextEditorSkeleton } from "src/components/rich-text-editor/rich-text-editor-skeleton";
import { IRichTextEditor, TRichTextEditorData } from "src/components/rich-text-editor/rich-text-editor.types";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";

export interface IWriteCommentModalShellProps extends PropsWithChildren {
    content: string;
    characterCountLimit?: number;
    editorPlaceholder: string;
    articlePreviewUrl: string | undefined;
    isPendingSave: boolean;
    onSave?: (params: TRichTextEditorData) => void;
    onFocus?: IRichTextEditor["onFocus"];
    onBlur?: IRichTextEditor["onBlur"];
    autofocus?: boolean;
}

const LazyLoadedRichTextEditor = dynamic<IRichTextEditor>(
    () => import("src/components/rich-text-editor/rich-text-editor").then(mod => mod.RichTextEditor),
    {
        loading: RichTextEditorSkeleton,
        ssr: false,
    }
);

export const WriteCommentModalShell = ({
    content,
    characterCountLimit,
    editorPlaceholder,
    articlePreviewUrl,
    children,
    isPendingSave,
    onSave,
    onFocus,
    onBlur,
    autofocus,
}: IWriteCommentModalShellProps) => {
    const { t } = useTranslation("comment");
    const toast = useWfToast();
    const parentRef = useRef<HTMLDivElement | null>(null);

    return (
        <Box width="100%" ref={parentRef}>
            {children}
            {/* box with fixed height is added to avoid jumping when react remounts the component */}
            <Box minHeight={["unset", "280px"]}>
                <LazyLoadedRichTextEditor
                    parentRef={parentRef}
                    content={content}
                    characterCountLimit={characterCountLimit}
                    onSave={params => {
                        if (params.characterLimitExceededByCount) {
                            toast({
                                status: EnumToastStatus.Error,
                                title: t("comment-content-character-count-exceeded-limit", { 0: params.characterLimitExceededByCount }),
                            });
                        } else {
                            onSave?.(params);
                        }
                    }}
                    onFocus={() => onFocus?.()}
                    onBlur={() => onBlur?.()}
                    placeholder={editorPlaceholder}
                    articlePreviewUrl={articlePreviewUrl}
                    isPendingSave={isPendingSave}
                    autofocus={autofocus}
                />
            </Box>
        </Box>
    );
};
